import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Alert,
  Snackbar,
  Divider
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  SupervisedUserCircle as SubUserIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import ConfirmDeleteDialog from '../../components/Common/ConfirmDeleteDialog';

// Yellow action button matching the design
const YBtn = ({ children, onClick, disabled, hidden }) => (
  <Box
    component="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      visibility: hidden ? 'hidden' : 'visible',
      minWidth: 36,
      width: 36,
      height: 36,
      p: 0,
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1,
      bgcolor: disabled ? '#e0e0e0' : '#f5c518',
      color: disabled ? '#aaa' : '#000',
      border: 'none',
      borderRadius: 1,
      cursor: disabled ? 'default' : 'pointer',
      flexShrink: 0,
      '&:hover': { bgcolor: disabled ? '#e0e0e0' : '#e0b800' },
    }}
  >
    {children}
  </Box>
);

// Admin email list — read-only display, no buttons
const AdminEmailList = ({ rows }) => (
  <Box>
    {rows.filter(r => !r.isNew || r.email.trim()).map((row, index) => (
      <Box key={row.key} sx={{ py: 1.5, px: 2, borderBottom: index < rows.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
        <Typography variant="body1">{row.email}</Typography>
      </Box>
    ))}
  </Box>
);

// SubUser email row list with − / + buttons
// Rules: − shown on all rows except single empty row; + shown only on last row
// currentUserEmail + isMainAdmin: sub-users can only edit their own row
const SubUserRowList = ({ rows, onChangeEmail, onAdd, onDelete, disabled, rowErrors = {}, onClearError, currentUserEmail, isMainAdmin }) => (
  <Box>
    {rows.map((row, index) => {
      const isLast = index === rows.length - 1;
      const noDataState = rows.length === 1 && !row.email.trim();
      const rowError = rowErrors[row.key];
      const isOwnRow = row.email === currentUserEmail;

      return (
        <Box key={row.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: rowError ? 0.5 : 1 }}>
          <TextField
            size="small"
            value={row.email}
            onChange={e => {
              onChangeEmail(index, e.target.value);
              if (rowError && onClearError) onClearError(row.key);
            }}
            placeholder="Enter email"
            error={!!rowError}
            helperText={rowError || ''}
            sx={{ flex: 1 }}
            InputProps={isOwnRow ? {
              endAdornment: (
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, pr: 0.5, whiteSpace: 'nowrap' }}>
                  You
                </Typography>
              )
            } : {}}
          />
          {isMainAdmin && (
            <Box sx={{ display: 'flex', gap: 1, mt: '4px' }}>
              <YBtn onClick={() => onDelete(index, row)} hidden={noDataState}>−</YBtn>
              <YBtn onClick={() => onAdd(index)} hidden={!isLast}>+</YBtn>
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

const emptyRow = () => ({ id: null, email: '', isNew: true, key: Date.now() + Math.random() });

const Administrators = () => {
  const { user } = useAuth();

  // Separate row states for each section
  const [adminRows, setAdminRows] = useState([emptyRow()]);
  const [subUserRows, setSubUserRows] = useState([emptyRow()]);

  const [subUsersDirty, setSubUsersDirty] = useState(false);

  const [subUserRowErrors, setSubUserRowErrors] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Custom confirm dialog for SubUser deletion
  const [confirmDelete, setConfirmDelete] = useState(null); // { row, index, apiDeleteFn, setRows, setDirty }

  // Contacts list (for isMainAdmin check)
  const [contacts, setContacts] = useState([]);

  const currentUserEmail = user?.email;
  const isMainAdmin = user?.userType === 'magic_admin' || (user?.userType !== 'sub_user' && contacts.some(c => c.email === currentUserEmail));

  const loadAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/admins');
      if (res.data.success) {
        const c = res.data.contacts || [];
        const s = res.data.subUsers || [];
        setContacts(c);

        setAdminRows(
          c.length > 0
            ? c.map(x => ({ id: x.id, email: x.email, isNew: false, key: x.id }))
            : [emptyRow()]
        );
        setSubUserRows(
          s.length > 0
            ? s.map(x => ({ id: x.id, email: x.email, role: x.role || 'admin', status: x.status ?? 1, full_name: x.full_name || '', isNew: false, key: x.id }))
            : [emptyRow()]
        );
        setSubUsersDirty(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load administrators');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  // --- Row helpers ---
  const makeAdder = (setRows, setDirty) => (afterIndex) => {
    setRows(prev => {
      const updated = [...prev];
      updated.splice(afterIndex + 1, 0, emptyRow());
      return updated;
    });
    setDirty(true);
  };

  const makeChanger = (setRows, setDirty) => (index, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], email: value };
      return updated;
    });
    setDirty(true);
  };

  const makeDeleter = (setRows, setDirty, apiDeleteFn) => async (index, row) => {
    if (!row.isNew) {
      // Show custom confirm dialog instead of window.confirm
      setConfirmDelete({ row, index, apiDeleteFn, setRows, setDirty });
      return;
    }
    setRows(prev => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.length > 0 ? updated : [emptyRow()];
    });
    setDirty(true);
  };

  const handleConfirmDelete = async () => {
    const { row, apiDeleteFn } = confirmDelete;
    setConfirmDelete(null);
    try {
      await apiDeleteFn(row.id);
      setSuccess('Deleted successfully');
      await loadAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  // --- SubUser row handlers ---
  const handleSubUserAdd = makeAdder(setSubUserRows, setSubUsersDirty);
  const handleSubUserChange = makeChanger(setSubUserRows, setSubUsersDirty);
  const handleSubUserDelete = makeDeleter(
    setSubUserRows, setSubUsersDirty,
    (id) => api.delete(`/admin/admins/${id}`)
  );

  // --- Save ---
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const saveRows = async (rows, saveNew, saveExisting, setRowErrors) => {
    const dirty = rows.filter(r => r.email.trim());
    const invalid = dirty.filter(r => !emailRegex.test(r.email.trim()));
    if (invalid.length > 0) {
      const errs = {};
      invalid.forEach(r => { errs[r.key] = 'Invalid email address'; });
      setRowErrors(errs);
      return false;
    }

    const errs = {};
    let ok = 0;
    for (const row of dirty) {
      try {
        if (row.isNew) await saveNew(row.email.trim());
        else await saveExisting(row);
        ok++;
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to save';
        errs[row.key] = msg;
      }
    }

    if (Object.keys(errs).length > 0) setRowErrors(errs);
    if (ok > 0) setSuccess(`Saved ${ok} record${ok > 1 ? 's' : ''}`);
    else if (Object.keys(errs).length === 0) setError('No changes to save');
    return ok > 0;
  };

  const handleSaveSubUsers = async () => {
    const saved = await saveRows(
      subUserRows,
      (email) => api.post('/admin/admins', { email, full_name: '', role: 'admin' }),
      (row) => api.put(`/admin/admins/${row.id}`, {
        email: row.email.trim(),
        full_name: row.full_name || '',
        role: row.role || 'admin',
        status: row.status ?? 1,
      }),
      setSubUserRowErrors
    );
    if (saved) await loadAdmins();
  };

  const isSubUser = !isMainAdmin;

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Administrators
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage administrators and sub-users for your organisation
        </Typography>
        {isMainAdmin && (
          <Alert severity="success" sx={{ mt: 2, maxWidth: 520 }}>
            You are a <strong>Main Administrator</strong>. You have full management access.
          </Alert>
        )}
        {isSubUser && !loading && (
          <Alert severity="info" sx={{ mt: 2, maxWidth: 520 }}>
            You are a <strong>Sub-User</strong>. Contact a main administrator to make changes.
          </Alert>
        )}
      </Box>

      {/* Admins section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AdminIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>Admins</Typography>
          <Chip
            label={contacts.length}
            size="small"
            sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600 }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading...</Typography>
        ) : (
          <AdminEmailList rows={adminRows} />
        )}
      </Paper>

      {/* SubUsers section */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SubUserIcon color="secondary" />
          <Typography variant="h6" fontWeight={600}>SubUsers</Typography>
          <Chip
            label={subUserRows.filter(r => !r.isNew).length}
            size="small"
            sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 600 }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading...</Typography>
        ) : (
          <>
            <SubUserRowList
              rows={subUserRows}
              onChangeEmail={handleSubUserChange}
              onAdd={handleSubUserAdd}
              onDelete={handleSubUserDelete}
              rowErrors={subUserRowErrors}
              onClearError={key => setSubUserRowErrors(prev => { const n = {...prev}; delete n[key]; return n; })}
              currentUserEmail={currentUserEmail}
              isMainAdmin={isMainAdmin}
            />
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={handleSaveSubUsers}
                disabled={!subUsersDirty}
              >
                Save
              </Button>
              {subUsersDirty && (
                <Button size="small" onClick={loadAdmins}>Cancel</Button>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* Custom delete confirm dialog */}
      <ConfirmDeleteDialog
        open={!!confirmDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={5000}
        onClose={() => { setSuccess(''); setError(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSuccess(''); setError(''); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Administrators;
